import {
	estimateDisplayAtom,
	estimateServiceBreakdownAtom,
	serviceAvailabilityAtom,
} from '@client/jotai/storefront/pricing';
import type { PersistedEstimate } from '@hooks/use-persisted-estimate';
import { usePersistedEstimate } from '@hooks/use-persisted-estimate';
import { Modal } from '@tkww/orion-web-ds-dialog';
import { compose } from '@xo-union/react-css-modules';
import { useAtomValue, useSetAtom } from 'jotai';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import type { ContactFormData, PricingEstimationService } from '../../../types';
import { useTrackEstimateModalDismissed } from '../../hooks/useTrackEstimateModalDismissed';
import { SERVICES } from '../../utils/estimateDisplay';
import type { WeddingDateData } from '../../utils/weddingDate';
import { ContactStep } from './ContactStep';
import { ErrorStep } from './ErrorStep';
import { EstimateFormModalHeader } from './EstimateFormModalHeader';
import { GuestCountStep } from './GuestCountStep';
import { LoadingStep } from './LoadingStep';
import { ServicesStep } from './ServicesStep';
import { WeddingDateStep } from './WeddingDateStep';
import { useCalculateEstimate } from './hooks/useCalculateEstimate';
import { useEstimateFormNavigation } from './hooks/useEstimateFormNavigation';
import { useEstimateFormProfile } from './hooks/useEstimateFormProfile';
import { useProfilePrefill } from './hooks/useProfilePrefill';
import { useSubmitContact } from './hooks/useSubmitContact';
import Styles from './styles.scss';
import { persistEstimateData } from './utils/persistEstimateData';

type FormView = 'form' | 'loading' | 'error';

interface EstimateFormStepsProps {
	currentStep: number;
	formView: FormView;
	selectedServices: PricingEstimationService[];
	setSelectedServices: (services: PricingEstimationService[]) => void;
	guestCount: string;
	setGuestCount: (count: string) => void;
	weddingDate: WeddingDateData;
	setWeddingDate: (date: WeddingDateData) => void;
	contactData: ContactFormData;
	setContactData: (data: ContactFormData) => void;
	handleNext: () => void;
	handleSubmitContact: () => void;
	isSubmitting: boolean;
	setFormView: (view: FormView) => void;
	onClose: () => void;
}

const EstimateFormSteps = ({
	currentStep,
	formView,
	selectedServices,
	setSelectedServices,
	guestCount,
	setGuestCount,
	weddingDate,
	setWeddingDate,
	contactData,
	setContactData,
	handleNext,
	handleSubmitContact,
	isSubmitting,
	setFormView,
	onClose,
}: EstimateFormStepsProps) => (
	<div className={Styles.stepContent}>
		{formView === 'form' && (
			<React.Fragment key={currentStep}>
				{currentStep === 1 && (
					<ServicesStep
						selectedServices={selectedServices}
						onChange={setSelectedServices}
						onNext={handleNext}
					/>
				)}
				{currentStep === 2 && (
					<GuestCountStep
						guestCount={guestCount}
						onChange={setGuestCount}
						onNext={handleNext}
					/>
				)}
				{currentStep === 3 && (
					<WeddingDateStep
						weddingDate={weddingDate}
						onChange={setWeddingDate}
						onNext={handleNext}
					/>
				)}
				{currentStep === 4 && (
					<ContactStep
						contactData={contactData}
						onChange={setContactData}
						onSubmit={handleSubmitContact}
						isSubmitting={isSubmitting}
					/>
				)}
			</React.Fragment>
		)}
		{formView === 'loading' && <LoadingStep />}
		{formView === 'error' && (
			<ErrorStep
				onRetry={() => {
					setFormView('form');
					handleSubmitContact();
				}}
			/>
		)}
	</div>
);

export interface EstimateFormModalProps {
	isModalOpen: boolean;
	closeCallback: () => void;
	onNewUserCreated?: () => void;
}

export const EstimateFormModal = ({
	isModalOpen,
	closeCallback,
	onNewUserCreated,
}: EstimateFormModalProps) => {
	const profile = useEstimateFormProfile();
	const { hasContactedVendor, vendorId } = profile;

	const storefrontId = useSelector(
		(state: Redux.State) =>
			state.rfq.storefrontId || state.vendor.vendorRaw?.id || '',
	);
	const trackModalDismissed = useTrackEstimateModalDismissed();
	const { setEstimate: persistEstimate } = usePersistedEstimate(storefrontId);
	const setEstimateDisplay = useSetAtom(estimateDisplayAtom);
	const setEstimateServiceBreakdown = useSetAtom(estimateServiceBreakdownAtom);

	const [currentStep, setCurrentStep] = useState(1);
	const [formView, setFormView] = useState<'form' | 'loading' | 'error'>(
		'form',
	);
	const {
		selectedServices,
		setSelectedServices,
		guestCount,
		setGuestCount,
		weddingDate,
		setWeddingDate,
		contactData,
		setContactData,
	} = useProfilePrefill(profile);

	const estimate = useCalculateEstimate(
		guestCount,
		weddingDate,
		selectedServices,
	);

	const serviceAvailability = useAtomValue(serviceAvailabilityAtom);
	const unavailableServices = useMemo(
		() =>
			SERVICES.filter(({ key }) => !serviceAvailability[key]).map(
				({ key }) => key,
			),
		[serviceAvailability],
	);

	const persistEstimateOnReady = useCallback(() => {
		if (!estimate) return;
		persistEstimateData({
			estimate,
			weddingDate,
			guestCount: Number(guestCount) || 0,
			rawGuestCount: guestCount,
			selectedServices,
			unavailableServices,
			storefrontId,
			setEstimateDisplay,
			setEstimateServiceBreakdown,
			persistEstimate,
		});
	}, [
		estimate,
		weddingDate,
		guestCount,
		selectedServices,
		unavailableServices,
		storefrontId,
		persistEstimate,
		setEstimateDisplay,
		setEstimateServiceBreakdown,
	]);

	const persistEstimateToStorage = useCallback(() => {
		if (!estimate || !storefrontId) return;
		const weddingDatePersisted: PersistedEstimate['weddingDate'] =
			weddingDate.dateOption === 'specificDate'
				? { type: 'exact', date: weddingDate.specificDate }
				: {
						type: 'month-year',
						month: weddingDate.month,
						year: Number(weddingDate.year),
					};
		persistEstimate({
			storefrontId,
			guestCount: Number(guestCount) || 0,
			weddingDate: weddingDatePersisted,
			selections: Object.fromEntries(selectedServices.map((s) => [s, true])),
			calculatedTotal: {
				min: estimate.totalCost.minCost,
				max: estimate.totalCost.maxCost,
				isBelowMinimumSpend: estimate.totalCost.isBelowMinimumSpend,
				originalMinCost: estimate.totalCost.originalMinCost,
			},
			unavailableServices,
			lastUpdated: new Date().toISOString(),
		});
	}, [
		estimate,
		weddingDate,
		guestCount,
		selectedServices,
		unavailableServices,
		storefrontId,
		persistEstimate,
	]);

	const onModalClosed = useCallback(
		() => trackModalDismissed(currentStep, { completed: true }),
		[trackModalDismissed, currentStep],
	);

	const { totalSteps, handleBack, handleNext } = useEstimateFormNavigation({
		currentStep,
		setCurrentStep,
		formView,
		setFormView,
		hasContactedVendor,
		estimate,
		closeCallback,
		onEstimateReady: persistEstimateOnReady,
		onEstimateCalculated: persistEstimateToStorage,
		onModalClosed,
	});

	const { handleSubmitContact, isSubmitting } = useSubmitContact({
		weddingDate,
		guestCount,
		selectedServices,
		unavailableServices,
		estimate,
		storefrontId,
		vendorId,
		profile,
		contactData,
		setFormView,
		setCurrentStep,
		setSelectedServices,
		setGuestCount,
		setWeddingDate,
		setContactData,
		closeCallback,
		onNewUserCreated,
	});

	const prevIsModalOpen = useRef(isModalOpen);
	useEffect(() => {
		if (isModalOpen && !prevIsModalOpen.current) {
			setFormView('form');
			setCurrentStep(1);
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen]);

	if (!isModalOpen) return null;

	const handleClose = () => {
		trackModalDismissed(currentStep);
		closeCallback();
	};

	return (
		<Modal
			size="sm"
			isOpen={isModalOpen}
			onClose={handleClose}
			onClickOutside={handleClose}
			autoFocus
			classes={compose({
				'anchor-container': Styles.anchorContainer,
				'button-container': Styles.buttonContainer,
				modal: Styles.modal,
				popup: Styles.popup,
			})}
		>
			<div className={Styles.modalContent} data-testid="estimate-form-modal">
				<EstimateFormModalHeader
					currentStep={currentStep}
					totalSteps={totalSteps}
					showBack={
						currentStep > 1 && currentStep <= totalSteps && formView === 'form'
					}
					showProgress={formView === 'form' && currentStep <= totalSteps}
					onBack={handleBack}
					onClose={handleClose}
				/>
				<EstimateFormSteps
					currentStep={currentStep}
					formView={formView}
					selectedServices={selectedServices}
					setSelectedServices={setSelectedServices}
					guestCount={guestCount}
					setGuestCount={setGuestCount}
					weddingDate={weddingDate}
					setWeddingDate={setWeddingDate}
					contactData={contactData}
					setContactData={setContactData}
					handleNext={handleNext}
					handleSubmitContact={handleSubmitContact}
					isSubmitting={isSubmitting}
					setFormView={setFormView}
					onClose={handleClose}
				/>
			</div>
		</Modal>
	);
};
